/**
 * RequirePermission — route/page-level RBAC guard.
 *
 * Usage in App.js:
 *   <Route path="transactions" element={
 *     <RequirePermission anyOf={["transactions.view"]}>
 *       <Transactions />
 *     </RequirePermission>
 *   } />
 *
 * If the user lacks the required permissions, renders <PermissionDenied />.
 * If the RBAC context is still loading, renders a small placeholder.
 */
import React from "react";
import { useRbac } from "../lib/rbac";
import PermissionDenied from "./PermissionDenied";

export default function RequirePermission({ perm, anyOf, allOf, children }) {
  const { hasPerm, hasAny, hasAll, loaded } = useRbac();
  if (!loaded) {
    return (
      <div className="p-8 font-mono text-xs tracking-widest text-zinc-500" data-testid="rbac-loading">
        LOADING PERMISSIONS…
      </div>
    );
  }
  let ok = true;
  if (perm) ok = ok && hasPerm(perm);
  if (anyOf && anyOf.length) ok = ok && hasAny(...anyOf);
  if (allOf && allOf.length) ok = ok && hasAll(...allOf);
  if (!ok) {
    const required = [perm, ...(anyOf || []), ...(allOf || [])].filter(Boolean);
    return <PermissionDenied required={required} />;
  }
  return children;
}
